import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// ReportFiltersPanel combines definition-provided filters with runtime operator
// filters so users can see which constraints are baked into the report versus
// which ones they added interactively.
const formatFilter = (filter) => {
  const field = filter?.field || 'field';
  const op = filter?.op || 'op';
  const value = filter?.value;
  const displayValue = Array.isArray(value) ? value.join(', ') : value ?? 'N/A';
  return `${field} ${op} ${displayValue}`;
};

const parseFilterValue = (op, raw) => {
  if (raw == null || raw === '') return raw;
  if (op === 'in' || op === 'not_in' || op === 'between') {
    return String(raw)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }
  return raw;
};

const isNoValueOperator = (op) => ['exists', 'not_exists', 'is_duplicate', 'not_duplicate'].includes(op);

const getFilterValueInputProps = ({ fieldMetadata, op }) => {
  if (isNoValueOperator(op)) {
    return { disabled: true, placeholder: 'No value needed' };
  }
  const isDate = fieldMetadata?.type === 'date' || fieldMetadata?.filterInput === 'date';
  if (!isDate) return {};
  if (op === 'between') {
    return { placeholder: 'YYYY-MM-DD, YYYY-MM-DD' };
  }
  if (op === 'in' || op === 'not_in') {
    return { placeholder: 'YYYY-MM-DD, YYYY-MM-DD, ...' };
  }
  return {
    type: 'date',
    InputLabelProps: { shrink: true },
  };
};

const ReportFiltersPanel = ({ dataset, filters, runtimeFilters, onAddFilter, onRemoveFilter, onClearFilters }) => {
  const filterableFields = dataset?.filterableFields || [];
  const allowedOperators = dataset?.allowedOperators || [];
  const fieldMetadata = dataset?.fieldMetadata || {};
  const [draft, setDraft] = useState({ field: '', op: allowedOperators[0] || 'eq', value: '' });
  const selectedFieldMetadata = fieldMetadata[draft.field] || {};
  const valueInputProps = getFilterValueInputProps({ fieldMetadata: selectedFieldMetadata, op: draft.op });

  const allFilters = useMemo(() => {
    const definitionFilters = (filters || []).map((filter) => ({ ...filter, source: 'definition' }));
    const runtime = (runtimeFilters || []).map((filter, index) => ({
      ...filter,
      source: 'runtime',
      runtimeIndex: index,
    }));
    return [...definitionFilters, ...runtime];
  }, [filters, runtimeFilters]);

  const handleAdd = () => {
    if (!draft.field || !draft.op) return;
    const next = {
      field: draft.field,
      op: draft.op,
      value: isNoValueOperator(draft.op) ? null : parseFilterValue(draft.op, draft.value),
      source: 'runtime',
    };
    onAddFilter?.(next);
    setDraft((prev) => ({ ...prev, value: '' }));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }} data-testid="report-filters-panel">
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Filters
        </Typography>
        {allFilters.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No filters configured. Use default actions to refine results.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {allFilters.map((filter, index) => (
              <Chip
                key={`${filter.field}-${index}`}
                label={formatFilter(filter)}
                variant={filter.source === 'runtime' ? 'filled' : 'outlined'}
                onDelete={filter.source === 'runtime' ? () => onRemoveFilter?.(filter.runtimeIndex) : undefined}
              />
            ))}
          </Stack>
        )}
        {filterableFields.length > 0 && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Field</InputLabel>
              <Select
                native
                value={draft.field}
                label="Field"
                onChange={(event) => setDraft((prev) => ({ ...prev, field: event.target.value, value: '' }))}
                inputProps={{ 'data-testid': 'report-filter-field' }}
              >
                <option aria-label="No field selected" value="" />
                {filterableFields.map((field) => (
                  <option key={field} value={field} data-testid={`report-filter-field-option-${field}`}>
                    {field}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                native
                value={draft.op}
                label="Operator"
                onChange={(event) => setDraft((prev) => ({ ...prev, op: event.target.value, value: '' }))}
                inputProps={{ 'data-testid': 'report-filter-operator' }}
              >
                {allowedOperators.map((op) => (
                  <option key={op} value={op} data-testid={`report-filter-operator-option-${op}`}>
                    {op}
                  </option>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Value"
              inputProps={{ 'data-testid': 'report-filter-value' }}
              value={draft.value}
              onChange={(event) => setDraft((prev) => ({ ...prev, value: event.target.value }))}
              sx={{ minWidth: 200 }}
              {...valueInputProps}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={handleAdd} data-testid="report-filter-add">
                Add filter
              </Button>
              <Button variant="text" onClick={onClearFilters} data-testid="report-filter-clear-runtime">
                Clear runtime
              </Button>
            </Box>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          Dataset fields: {filterableFields.length ? filterableFields.join(', ') : 'No dataset fields registered.'}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default ReportFiltersPanel;
